/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * Dev Control Panel - Runtime Configuration for Voice System
 *
 * @fileoverview Admin-only development control panel for voice configuration
 * @version 1.0.0
 * @since v0.0.08
 *
 * PURPOSE:
 * Provide live toggle interface for all voice configuration options during
 * development and testing. Allows rapid experimentation without code changes.
 *
 * ACCESS CONTROL:
 * - Accessible via Settings menu (admin tier only)
 * - Requires admin role (user.roles includes 'admin')
 * - Redirects to dashboard if not authorized
 *
 * TOGGLE CATEGORIES:
 * 1. Voice Configuration (pathway, multi-speaker, MM trigger, persistence)
 * 2. Quality Metrics (live stats, jitter buffer presets)
 * 3. Discord Integration (command injection, relay events)
 * 4. WebSocket Monitor (event stream visualization)
 * 5. Config Management (save/load/reset configurations)
 *
 * FRONTEND INTEGRATION:
 * - Updates campaign.voice_settings via PUT /api/campaigns/{id}/voice-settings
 * - Changes propagate to active voice sessions via WebSocket
 * - VoiceControls/VoiceStatus components react to setting changes
 *
 * BACKEND INTEGRATION:
 * - PUT /api/dev/voice-pathway      (admin-only — persists to Supabase campaigns.campaign_settings)
 * - PUT /api/dev/multi-speaker-mode (admin-only — persists to Supabase campaigns.campaign_settings)
 * - POST /api/discord/command       (admin-only — Redis pub/sub relay to Discord bot)
 * - WebSocket: Subscribe to ALL event types for monitoring
 *
 * API AUTH PATTERN:
 *   Authorization: Bearer <access_token> header (from localStorage).
 *   credentials: 'include' for session cookie fallback.
 *   Graceful degradation: if API fails, localStorage state is still updated (optimistic UI).
 *
 * CAMPAIGN ID:
 *   Admin enters the target campaign_id via text input in Voice Configuration section.
 *   Persisted to localStorage (dev_controls_campaign_id) — survives page refreshes.
 *   Required for PUT endpoints; POST /api/discord/command does not use campaign_id.
 *
 * CHANGE LOG:
 * 2026-01-05 - Agent A   - Initial implementation
 * 2026-04-15 - Claude 4.6 - Phase 3: Wired PUT /voice-pathway, PUT /multi-speaker-mode,
 *                           POST /discord/command to backend endpoints.
 *                           Added campaignId state + input. Removed BACKEND_REQUIRED blocks.
 *                           PRD: shared/docs/Prompts/PRD_DevControlsBackend_v1.md § Phase 3
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { webSocketService } from '../services/websocket/websocketService';
import { voicePlaybackService, BUFFER_PRESETS } from '../services/voice';
import { RoleType } from '../config/roles';
import { AdminMonitoring } from '../components/AdminMonitoring';
// Added 2026-04-15: API base URL for backend endpoint calls (Phase 3)
// getApiBaseUrl() returns the base URL WITH /api suffix, no trailing slash.
// e.g. http://localhost:8080/api (dev) or https://mythboundadventurestudio.onrender.com/api (prod)
// PRD: shared/docs/Prompts/PRD_DevControlsBackend_v1.md § Phase 3
import { getApiBaseUrl } from '../utils/environment';

// ---------------------------------------------------------------------------
// API helpers (module-level — consistent with CampaignService pattern)
// ---------------------------------------------------------------------------

/** Base API URL — resolved once at module load */
const API_BASE = getApiBaseUrl();

/**
 * Build Authorization header from localStorage access_token.
 * Matches CampaignService.getAuthHeaders() pattern.
 * Added: 2026-04-15 (PRD_DevControlsBackend_v1.md § Phase 3)
 */
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ============================================================================
// TYPES
// ============================================================================

/**
 * Voice configuration state
 */
interface VoiceConfig {
  pathway: 'auto' | 'stt_llm_tts' | 'sts';
  multiSpeaker: 'parallel' | 'sequential' | 'priority' | 'player_level';
  mmTrigger: 'transcribe_all' | 'wake_word' | 'turn_based' | 'smart';
  sessionPersistence: 'db_persistence' | 'ephemeral' | 'grace_period';
  jitterBuffer: 'responsive' | 'balanced' | 'smooth';
}

/**
 * Quality metrics from voice services
 */
interface QualityMetrics {
  latency: number;
  jitter: number;
  packetLoss: number;
  bufferSize: number;
}

/**
 * WebSocket event for monitoring
 */
interface WebSocketEvent {
  id: string;
  timestamp: string;
  category: 'VOICE' | 'CAMPAIGN' | 'DICE' | 'OTHER';
  event: string;
  data?: unknown;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const LOCALSTORAGE_KEY_CONFIG = 'dev_controls_current';
const MAX_EVENTS = 100;

const DEFAULT_CONFIG: VoiceConfig = {
  pathway: 'auto',
  multiSpeaker: 'parallel',
  mmTrigger: 'transcribe_all',
  sessionPersistence: 'grace_period',
  jitterBuffer: 'balanced'
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * DevControls Page Component
 *
 * Admin-only development control panel for runtime voice configuration.
 * Provides toggles for voice pathways, multi-speaker modes, quality settings,
 * Discord integration, and WebSocket event monitoring.
 */
const DevControls: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo, isAuthenticated, isLoading } = useAuth();
  
  // Voice configuration state
  const [config, setConfig] = useState<VoiceConfig>(() => {
    const saved = localStorage.getItem(LOCALSTORAGE_KEY_CONFIG);
    return saved ? JSON.parse(saved) : DEFAULT_CONFIG;
  });

  // Quality metrics state
  const [metrics, setMetrics] = useState<QualityMetrics>({
    latency: 0,
    jitter: 0,
    packetLoss: 0,
    bufferSize: 150
  });

  // Discord integration state
  const [discordCommand, setDiscordCommand] = useState('');
  const [discordEvents, setDiscordEvents] = useState<string[]>([
    '• No recent events'
  ]);

  // Campaign ID for backend API calls (PUT /api/dev/* endpoints).
  // Admin enters this manually — dev-tool pattern (Option A, approved by Mr. Louis 2026-04-15).
  // Persisted to localStorage so it survives page refreshes and re-mounts.
  // POST /api/discord/command does NOT require campaign_id.
  // Added: 2026-04-15 (PRD_DevControlsBackend_v1.md § Phase 3)
  const [campaignId, setCampaignId] = useState<string>(() =>
    localStorage.getItem('dev_controls_campaign_id') || ''
  );
  // Persist campaignId changes to localStorage
  useEffect(() => {
    localStorage.setItem('dev_controls_campaign_id', campaignId);
  }, [campaignId]);

  // WebSocket monitor state
  const [wsEvents, setWsEvents] = useState<WebSocketEvent[]>([]);
  const [wsFilter, setWsFilter] = useState<string>('all');
  const [isPaused, setIsPaused] = useState(false);
  const pausedRef = useRef(isPaused);

  // Saved configs state
  const [savedConfigs, setSavedConfigs] = useState<string[]>([]);

  // Update ref when isPaused changes
  useEffect(() => {
    pausedRef.current = isPaused;
  }, [isPaused]);

  // ==========================================================================
  // ACCESS CONTROL
  // ==========================================================================

  // Check if user has admin role
  const isAdmin = userInfo?.roles?.includes(RoleType.ADMIN) || false;

  // Redirect non-admins
  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !isAdmin)) {
      console.log('[DevControls] Access denied. Redirecting to dashboard.');
      navigate('/dashboard');
    }
  }, [isLoading, isAuthenticated, isAdmin, navigate]);

  // ==========================================================================
  // CONFIG PERSISTENCE
  // ==========================================================================

  // Auto-save config changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(LOCALSTORAGE_KEY_CONFIG, JSON.stringify(config));
    }, 2000);

    return () => clearTimeout(timer);
  }, [config]);

  // Load saved config names
  useEffect(() => {
    const configs: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('dev_controls_config_')) {
        configs.push(key.replace('dev_controls_config_', ''));
      }
    }
    setSavedConfigs(configs);
  }, []);

  // ==========================================================================
  // QUALITY METRICS SUBSCRIPTION
  // ==========================================================================

  useEffect(() => {
    // Subscribe to voice metrics from VoicePlaybackService
    const metricsInterval = setInterval(() => {
      const stats = voicePlaybackService.getStats();
      setMetrics({
        latency: stats.averageLatencyMs,
        jitter: stats.jitterMs,
        packetLoss: stats.packetsDropped > 0 
          ? (stats.packetsDropped / (stats.packetsReceived || 1)) * 100 
          : 0,
        bufferSize: stats.currentBufferMs
      });
    }, 1000);

    return () => clearInterval(metricsInterval);
  }, []);

  // ==========================================================================
  // WEBSOCKET EVENT MONITORING
  // ==========================================================================

  const addEvent = useCallback((category: WebSocketEvent['category'], event: string, data?: unknown) => {
    if (pausedRef.current) return;

    const newEvent: WebSocketEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toLocaleTimeString(),
      category,
      event,
      data
    };

    setWsEvents(prev => {
      const updated = [newEvent, ...prev];
      return updated.slice(0, MAX_EVENTS);
    });
  }, []);

  useEffect(() => {
    // Subscribe to various WebSocket events for monitoring
    const voiceHandler = (data: unknown) => addEvent('VOICE', 'voice_data', data);
    const voiceJoinHandler = (data: unknown) => addEvent('VOICE', 'voice_session_joined', data);
    const voiceLeaveHandler = (data: unknown) => addEvent('VOICE', 'voice_session_left', data);
    const voiceModeHandler = (data: unknown) => addEvent('VOICE', 'voice_mode_changed', data);
    const campaignHandler = (data: unknown) => addEvent('CAMPAIGN', 'campaign_update', data);
    const diceHandler = (data: unknown) => addEvent('DICE', 'dice_roll', data);
    const genericHandler = (data: unknown) => addEvent('OTHER', 'message', data);

    webSocketService.subscribe('voice_data', voiceHandler);
    webSocketService.subscribe('voice_session_joined', voiceJoinHandler);
    webSocketService.subscribe('voice_session_left', voiceLeaveHandler);
    webSocketService.subscribe('voice_mode_changed', voiceModeHandler);
    webSocketService.subscribe('campaign_update', campaignHandler);
    webSocketService.subscribe('dice_roll', diceHandler);
    webSocketService.subscribe('message', genericHandler);

    return () => {
      webSocketService.unsubscribe('voice_data', voiceHandler);
      webSocketService.unsubscribe('voice_session_joined', voiceJoinHandler);
      webSocketService.unsubscribe('voice_session_left', voiceLeaveHandler);
      webSocketService.unsubscribe('voice_mode_changed', voiceModeHandler);
      webSocketService.unsubscribe('campaign_update', campaignHandler);
      webSocketService.unsubscribe('dice_roll', diceHandler);
      webSocketService.unsubscribe('message', genericHandler);
    };
  }, [addEvent]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  /**
   * Apply voice pathway change.
   * Optimistic UI: updates local state immediately, then persists to Supabase via API.
   * Graceful degradation: if API fails, localStorage is still updated (no crash, no reset).
   * Contract: shared/contracts/campaign/v1/campaign-management.yaml § voice_pathway
   * Backend: PUT /api/dev/voice-pathway (admin-only, 60 req/min)
   * Added: 2026-04-15 (PRD_DevControlsBackend_v1.md § Phase 3)
   */
  const handlePathwayChange = async (newPathway: VoiceConfig['pathway']) => {
    // Optimistic UI update — localStorage auto-saved by the config useEffect above
    setConfig(prev => ({ ...prev, pathway: newPathway }));

    // Persist to Supabase via backend — only if campaign_id is set
    if (campaignId) {
      try {
        const res = await fetch(`${API_BASE}/dev/voice-pathway`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ campaign_id: campaignId, voice_pathway: newPathway }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.warn('[DevControls] voice-pathway API save failed:', res.status, err);
        }
      } catch (err) {
        console.warn('[DevControls] voice-pathway API unreachable:', err);
      }
    }

    addEvent('OTHER', 'config_change', { type: 'pathway', value: newPathway });
  };

  /**
   * Apply multi-speaker mode change.
   * Optimistic UI: updates local state immediately, then persists to Supabase via API.
   * Graceful degradation: if API fails, localStorage is still updated (no crash, no reset).
   * Contracts: shared/contracts/voice/v1/speaker-mapping.yaml § multi_speaker_mode
   *            shared/contracts/campaign/v1/campaign-management.yaml § multi_speaker_mode
   * Backend: PUT /api/dev/multi-speaker-mode (admin-only, 60 req/min)
   * Added: 2026-04-15 (PRD_DevControlsBackend_v1.md § Phase 3)
   */
  const handleMultiSpeakerChange = async (newMode: VoiceConfig['multiSpeaker']) => {
    // Optimistic UI update — localStorage auto-saved by the config useEffect above
    setConfig(prev => ({ ...prev, multiSpeaker: newMode }));

    // Persist to Supabase via backend — only if campaign_id is set
    if (campaignId) {
      try {
        const res = await fetch(`${API_BASE}/dev/multi-speaker-mode`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ campaign_id: campaignId, multi_speaker_mode: newMode }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.warn('[DevControls] multi-speaker-mode API save failed:', res.status, err);
        }
      } catch (err) {
        console.warn('[DevControls] multi-speaker-mode API unreachable:', err);
      }
    }

    addEvent('OTHER', 'config_change', { type: 'multiSpeaker', value: newMode });
  };

  /**
   * Apply MM trigger mode change
   */
  const handleMmTriggerChange = (newTrigger: VoiceConfig['mmTrigger']) => {
    setConfig(prev => ({ ...prev, mmTrigger: newTrigger }));
    addEvent('OTHER', 'config_change', { type: 'mmTrigger', value: newTrigger });
  };

  /**
   * Apply session persistence mode change
   */
  const handlePersistenceChange = (newPersistence: VoiceConfig['sessionPersistence']) => {
    setConfig(prev => ({ ...prev, sessionPersistence: newPersistence }));
    addEvent('OTHER', 'config_change', { type: 'sessionPersistence', value: newPersistence });
  };

  /**
   * Apply jitter buffer preset change
   */
  const handleJitterBufferChange = (preset: VoiceConfig['jitterBuffer']) => {
    setConfig(prev => ({ ...prev, jitterBuffer: preset }));
    voicePlaybackService.setBufferPreset(preset);
    addEvent('OTHER', 'config_change', { type: 'jitterBuffer', value: preset });
  };

  /**
   * Send a whitelisted Discord bot command via POST /api/discord/command.
   *
   * Command format: first word = command name, rest = message text or channel name.
   * Examples:
   *   "announce Heading into session 3!"  → cmd=announce, args={message: "Heading into session 3!"}
   *   "voice-join General"                → cmd=voice-join, args={channel: "General"}
   *   "status"                            → cmd=status, args={}
   *
   * Backend: POST /api/discord/command (admin-only, 30 req/min)
   * Relay: Redis pub/sub → discord_bot._start_command_listener → _dispatch_discord_command
   * Whitelist: announce, status, voice-list, voice-join, voice-leave (enforced server-side)
   * Graceful degradation: local event log updated even if API is unreachable.
   * Updated: 2026-04-16 — calls pollCommandResult() after successful queue
   *           (PRD_DiscordCommandAck_v1.md § Phase 2)
   * Added: 2026-04-15 (PRD_DevControlsBackend_v1.md § Phase 3)
   */

  /**
   * Polls GET /api/discord/command/{requestId}/result every 1 second for up to 10 attempts.
   * Prepends a result line to the Discord event log when the result arrives or timeout occurs.
   * Non-blocking — call without await so UI remains responsive during polling.
   *
   * @param requestId - The 8-char request ID returned by POST /api/discord/command
   * @param command   - The command name, used for display in the event log
   *
   * Result log formats (prepended, newest-first, matching existing event log pattern):
   *   ✓ Completed: {command} — {detail}              (bot executed successfully)
   *   ✗ Failed: {command} — {detail}                 (bot returned success=false)
   *   ⏳ Pending: {command} — no response within 10s  (timeout after 10 polls)
   *
   * Polling: 10 attempts × 1000ms interval = 10s max.
   * 202 responses continue polling; 200 resolves immediately.
   * Network errors during polling are swallowed — polling continues until timeout.
   * Multiple concurrent polls are independent (each runs its own loop with its own requestId).
   *
   * Backend: GET /api/discord/command/{requestId}/result (admin_limit, 60/min)
   * Redis: discord:cmd:result:{requestId} (60s TTL, written by discord_bot._publish_command_result)
   * Added: 2026-04-16 (PRD_DiscordCommandAck_v1.md § Phase 2)
   */
  const pollCommandResult = async (requestId: string, command: string): Promise<void> => {
    const MAX_ATTEMPTS = 10;
    const INTERVAL_MS = 1000;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise<void>(resolve => setTimeout(resolve, INTERVAL_MS));
      try {
        const res = await fetch(`${API_BASE}/discord/command/${requestId}/result`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.status === 200) {
          const data = await res.json();
          const symbol = data.success ? '✓' : '✗';
          const label = data.success ? 'Completed' : 'Failed';
          setDiscordEvents(prev => [
            `${symbol} ${label}: ${command} — ${data.detail}`,
            ...prev.slice(0, 9),
          ]);
          return;
        }
        // 202 = still pending — continue polling
      } catch {
        // Network error — continue polling until timeout
      }
    }
    // Timeout: result did not arrive within 10s
    setDiscordEvents(prev => [
      `⏳ Pending: ${command} — no response within 10s`,
      ...prev.slice(0, 9),
    ]);
  };

  const handleDiscordCommand = async () => {
    if (!discordCommand.trim()) return;

    // Parse: first word = command name, remainder = message text or channel name
    const parts = discordCommand.trim().split(/\s+/);
    const cmd = parts[0];
    const rest = parts.slice(1).join(' ');
    const args: Record<string, string> = {};
    if (rest) {
      // voice-join uses "channel" key; announce (and anything else) uses "message"
      args[cmd === 'voice-join' ? 'channel' : 'message'] = rest;
    }

    try {
      const res = await fetch(`${API_BASE}/discord/command`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ command: cmd, args }),
      });

      if (res.ok) {
        const data = await res.json();
        setDiscordEvents(prev => [
          `• Queued: ${cmd} (req: ${data.request_id})`,
          ...prev.slice(0, 9),
        ]);
        // Non-blocking poll for result — does not await so UI is never blocked.
        // Appends ✓/✗/⏳ line to event log when bot confirms execution (or 10s timeout).
        // Added: 2026-04-16 (PRD_DiscordCommandAck_v1.md § Phase 2)
        pollCommandResult(data.request_id, cmd);
      } else {
        const err = await res.json().catch(() => ({}));
        setDiscordEvents(prev => [
          `• Failed (${res.status}): ${err.detail || cmd}`,
          ...prev.slice(0, 9),
        ]);
      }
    } catch (err) {
      console.warn('[DevControls] Discord command API unreachable:', err);
      // Graceful degradation: log locally so admin knows the attempt was made
      setDiscordEvents(prev => [
        `• (offline) ${discordCommand}`,
        ...prev.slice(0, 9),
      ]);
    }

    addEvent('OTHER', 'discord_command', { command: cmd, args });
    setDiscordCommand('');
  };

  /**
   * Export WebSocket event log as JSON
   */
  const exportEventLog = () => {
    const exportData = {
      exported_at: new Date().toISOString(),
      event_count: wsEvents.length,
      events: wsEvents
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ws_events_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    addEvent('OTHER', 'events_exported', { count: wsEvents.length });
  };

  /**
   * Save current configuration with custom name
   */
  const saveConfig = (name: string) => {
    const configData = {
      ...config,
      saved_at: new Date().toISOString(),
      name
    };

    localStorage.setItem(`dev_controls_config_${name}`, JSON.stringify(configData));
    setSavedConfigs(prev => [...prev.filter(n => n !== name), name]);
    addEvent('OTHER', 'config_saved', { name });
  };

  /**
   * Load a saved configuration
   */
  const loadConfig = (name: string) => {
    const saved = localStorage.getItem(`dev_controls_config_${name}`);
    if (saved) {
      const parsed = JSON.parse(saved);
      setConfig({
        pathway: parsed.pathway || DEFAULT_CONFIG.pathway,
        multiSpeaker: parsed.multiSpeaker || DEFAULT_CONFIG.multiSpeaker,
        mmTrigger: parsed.mmTrigger || DEFAULT_CONFIG.mmTrigger,
        sessionPersistence: parsed.sessionPersistence || DEFAULT_CONFIG.sessionPersistence,
        jitterBuffer: parsed.jitterBuffer || DEFAULT_CONFIG.jitterBuffer
      });
      addEvent('OTHER', 'config_loaded', { name });
    }
  };

  /**
   * Reset configuration to defaults
   */
  const resetConfig = () => {
    setConfig(DEFAULT_CONFIG);
    addEvent('OTHER', 'config_reset', {});
  };

  /**
   * Filter events by category
   */
  const filteredEvents = wsFilter === 'all' 
    ? wsEvents 
    : wsEvents.filter(e => e.category.toLowerCase() === wsFilter);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  // This shouldn't render due to redirect, but safety fallback
  if (!isAdmin) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Dev Control Panel</h1>
        <button 
          onClick={() => navigate('/dashboard')}
          style={styles.closeButton}
          aria-label="Close panel"
        >
          ×
        </button>
      </div>

      <div style={styles.content}>
        {/* Admin Monitoring Panel */}
        <AdminMonitoring />

        {/* Voice Configuration Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Voice Configuration</h2>

          {/* Campaign ID — required for PUT /api/dev/* backend persistence
              Admin enters the target campaign_id once; saved to localStorage.
              Voice pathway and multi-speaker changes only reach Supabase when this is set.
              Added: 2026-04-15 (PRD_DevControlsBackend_v1.md § Phase 3) */}
          <div style={styles.configRow}>
            <label style={styles.configLabel} htmlFor="dev-campaign-id">
              Campaign ID (for API persistence):
            </label>
            <input
              id="dev-campaign-id"
              type="text"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              placeholder="camp_abc123"
              style={styles.input}
              aria-label="Target campaign ID for voice config API persistence"
            />
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px', marginBottom: 0 }}>
              Enter a campaign ID to persist voice settings to Supabase. Saved to localStorage.
              Leave empty to update local UI only.
            </p>
          </div>

          <div style={styles.divider} />

          {/* Voice Pathway */}
          <div style={styles.configRow}>
            <label style={styles.configLabel}>Voice Pathway:</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="pathway"
                  value="auto"
                  checked={config.pathway === 'auto'}
                  onChange={() => handlePathwayChange('auto')}
                  style={styles.radio}
                />
                Auto-Switch (Recommended)
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="pathway"
                  value="stt_llm_tts"
                  checked={config.pathway === 'stt_llm_tts'}
                  onChange={() => handlePathwayChange('stt_llm_tts')}
                  style={styles.radio}
                />
                STT→LLM→TTS Only
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="pathway"
                  value="sts"
                  checked={config.pathway === 'sts'}
                  onChange={() => handlePathwayChange('sts')}
                  style={styles.radio}
                />
                STS Only (OpenAI Realtime)
              </label>
            </div>
          </div>

          <div style={styles.divider} />

          {/* Multi-Speaker Mode */}
          <div style={styles.configRow}>
            <label style={styles.configLabel}>Multi-Speaker Mode:</label>
            <select
              value={config.multiSpeaker}
              onChange={(e) => handleMultiSpeakerChange(e.target.value as VoiceConfig['multiSpeaker'])}
              style={styles.select}
            >
              <option value="parallel">Parallel (capture all simultaneously)</option>
              <option value="sequential">Sequential (queue speakers FIFO)</option>
              <option value="priority">Priority (GM &gt; players)</option>
              <option value="player_level">Player-Level (individual toggles)</option>
            </select>
          </div>

          <div style={styles.divider} />

          {/* MM Trigger Mode */}
          <div style={styles.configRow}>
            <label style={styles.configLabel}>MM™ Trigger Mode:</label>
            <select
              value={config.mmTrigger}
              onChange={(e) => handleMmTriggerChange(e.target.value as VoiceConfig['mmTrigger'])}
              style={styles.select}
            >
              <option value="transcribe_all">Transcribe All (real-time commentary)</option>
              <option value="wake_word">Wake Word Only ("Hey MM")</option>
              <option value="turn_based">Turn-Based (dice/GM cues)</option>
              <option value="smart">Smart Detection (context-aware)</option>
            </select>
          </div>

          <div style={styles.divider} />

          {/* Session Persistence */}
          <div style={styles.configRow}>
            <label style={styles.configLabel}>Session Persistence:</label>
            <select
              value={config.sessionPersistence}
              onChange={(e) => handlePersistenceChange(e.target.value as VoiceConfig['sessionPersistence'])}
              style={styles.select}
            >
              <option value="db_persistence">DB Persistence (full history)</option>
              <option value="ephemeral">Ephemeral (memory only)</option>
              <option value="grace_period">Grace Period (60s reconnect)</option>
            </select>
          </div>
        </section>

        {/* Quality Metrics Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Quality Metrics (Live)</h2>

          <div style={styles.metricsGrid}>
            <div style={styles.metricBox}>
              <div style={styles.metricLabel}>Latency</div>
              <div style={styles.metricValue}>{Math.round(metrics.latency)}ms</div>
            </div>
            <div style={styles.metricBox}>
              <div style={styles.metricLabel}>Jitter</div>
              <div style={styles.metricValue}>{Math.round(metrics.jitter)}ms</div>
            </div>
            <div style={styles.metricBox}>
              <div style={styles.metricLabel}>Packet Loss</div>
              <div style={{
                ...styles.metricValue,
                color: metrics.packetLoss > 5 ? '#ef4444' : metrics.packetLoss > 1 ? '#eab308' : '#22c55e'
              }}>
                {metrics.packetLoss.toFixed(1)}%
              </div>
            </div>
            <div style={styles.metricBox}>
              <div style={styles.metricLabel}>Buffer</div>
              <div style={styles.metricValue}>{metrics.bufferSize}ms</div>
            </div>
          </div>

          <div style={styles.presetButtons}>
            {Object.entries(BUFFER_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => handleJitterBufferChange(key as VoiceConfig['jitterBuffer'])}
                style={{
                  ...styles.presetButton,
                  ...(config.jitterBuffer === key ? styles.presetButtonActive : {})
                }}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </section>

        {/* Discord Integration Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Discord Integration</h2>

          <div style={styles.configRow}>
            <label style={styles.configLabel}>Command Injector:</label>
            <div style={styles.commandRow}>
              <input
                type="text"
                value={discordCommand}
                onChange={(e) => setDiscordCommand(e.target.value)}
                placeholder="/mythbound join #general-voice"
                style={styles.input}
                onKeyDown={(e) => e.key === 'Enter' && handleDiscordCommand()}
              />
              <button 
                onClick={handleDiscordCommand}
                style={styles.sendButton}
                disabled={!discordCommand.trim()}
              >
                Send
              </button>
            </div>
          </div>

          <div style={styles.eventLog}>
            <div style={styles.eventLogTitle}>Recent Discord Events:</div>
            <div style={styles.eventLogContent}>
              {discordEvents.map((event, i) => (
                <div key={i} style={styles.eventLogItem}>{event}</div>
              ))}
            </div>
          </div>
        </section>

        {/* WebSocket Event Monitor */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>WebSocket Event Monitor</h2>

          <div style={styles.filterRow}>
            {['voice', 'campaign', 'dice', 'all'].map(filter => (
              <button
                key={filter}
                onClick={() => setWsFilter(filter)}
                style={{
                  ...styles.filterButton,
                  ...(wsFilter === filter ? styles.filterButtonActive : {})
                }}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
            <button
              onClick={() => setIsPaused(!isPaused)}
              style={{
                ...styles.filterButton,
                ...(isPaused ? styles.pauseButtonActive : {})
              }}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
          </div>

          <div style={styles.eventStream}>
            {filteredEvents.length === 0 ? (
              <div style={styles.noEvents}>No events captured yet...</div>
            ) : (
              filteredEvents.map(event => (
                <div key={event.id} style={styles.eventRow}>
                  <span style={styles.eventTime}>{event.timestamp}</span>
                  <span style={{
                    ...styles.eventCategory,
                    backgroundColor: getCategoryColor(event.category)
                  }}>
                    {event.category}
                  </span>
                  <span style={styles.eventName}>{event.event}</span>
                </div>
              ))
            )}
          </div>

          <div style={styles.eventActions}>
            <button onClick={() => setWsEvents([])} style={styles.actionButton}>
              🗑️ Clear
            </button>
            <button onClick={exportEventLog} style={styles.actionButton}>
              📥 Export JSON
            </button>
          </div>
        </section>

        {/* Configuration Management */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Configuration Management</h2>

          <div style={styles.configActions}>
            <button
              onClick={() => {
                const name = prompt('Enter config name:');
                if (name) saveConfig(name);
              }}
              style={styles.actionButton}
            >
              💾 Save Config
            </button>
            <select
              onChange={(e) => e.target.value && loadConfig(e.target.value)}
              style={styles.configSelect}
              defaultValue=""
            >
              <option value="" disabled>Load Preset...</option>
              {savedConfigs.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <button onClick={resetConfig} style={styles.resetButton}>
              🔄 Reset All
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getCategoryColor(category: WebSocketEvent['category']): string {
  switch (category) {
    case 'VOICE':
      return 'rgba(34, 197, 94, 0.3)';
    case 'CAMPAIGN':
      return 'rgba(59, 130, 246, 0.3)';
    case 'DICE':
      return 'rgba(234, 179, 8, 0.3)';
    default:
      return 'rgba(139, 92, 246, 0.3)';
  }
}

// ============================================================================
// STYLES
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0f',
    color: '#fff',
    fontFamily: '"Inter", sans-serif'
  },

  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#0a0a0f'
  },

  loadingText: {
    color: '#a78bfa',
    fontSize: '18px'
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 32px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)'
  },

  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600',
    color: '#a78bfa',
    fontFamily: '"Cinzel", serif'
  },

  closeButton: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px 32px'
  },

  section: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    border: '1px solid rgba(255, 255, 255, 0.06)'
  },

  sectionTitle: {
    margin: '0 0 20px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#a78bfa',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },

  configRow: {
    marginBottom: '16px'
  },

  configLabel: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.8)'
  },

  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer'
  },

  radio: {
    accentColor: '#8b5cf6'
  },

  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },

  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    margin: '16px 0'
  },

  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '16px'
  },

  metricBox: {
    textAlign: 'center',
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '8px'
  },

  metricLabel: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase',
    marginBottom: '8px'
  },

  metricValue: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#fff'
  },

  presetButtons: {
    display: 'flex',
    gap: '12px'
  },

  presetButton: {
    flex: 1,
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'transparent',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.15s'
  },

  presetButtonActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    borderColor: '#8b5cf6',
    color: '#a78bfa'
  },

  commandRow: {
    display: 'flex',
    gap: '8px'
  },

  input: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    color: '#fff',
    fontSize: '13px'
  },

  sendButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },

  eventLog: {
    marginTop: '16px'
  },

  eventLogTitle: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.6)',
    marginBottom: '8px'
  },

  eventLogContent: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: '120px',
    overflowY: 'auto'
  },

  eventLogItem: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    padding: '4px 0'
  },

  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px'
  },

  filterButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'transparent',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '12px',
    cursor: 'pointer'
  },

  filterButtonActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    borderColor: '#8b5cf6',
    color: '#a78bfa'
  },

  pauseButtonActive: {
    backgroundColor: 'rgba(234, 179, 8, 0.2)',
    borderColor: '#eab308',
    color: '#fcd34d'
  },

  eventStream: {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: '300px',
    overflowY: 'auto',
    fontFamily: '"Fira Code", monospace',
    fontSize: '11px'
  },

  noEvents: {
    color: 'rgba(255, 255, 255, 0.4)',
    textAlign: 'center',
    padding: '24px'
  },

  eventRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
  },

  eventTime: {
    color: 'rgba(255, 255, 255, 0.4)',
    minWidth: '70px'
  },

  eventCategory: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },

  eventName: {
    color: 'rgba(255, 255, 255, 0.8)'
  },

  eventActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px'
  },

  actionButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'transparent',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '12px',
    cursor: 'pointer'
  },

  configActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },

  configSelect: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
    minWidth: '150px'
  },

  resetButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#fca5a5',
    fontSize: '12px',
    cursor: 'pointer'
  }
};

export default DevControls;

